import {
  MARKETING_PRODUCT_PROFILE,
  MARKETING_PRODUCT_RESPONSIBILITIES,
  MARKETING_PRODUCT_TOOLS,
  MARKETING_PRODUCT_TRAINING,
} from "~/lib/inbound-gpt/paths";
import { SITEGLUE_PRODUCT_NAME } from "~/lib/siteglue/customer-terms";

export type SiteGlueAgentSectionId =
  | "inbox"
  | "contacts"
  | "profile"
  | "team"
  | "channels"
  | "training"
  | "tools";

export type SiteGlueAgentSection = {
  id: SiteGlueAgentSectionId;
  dashboardLabel: string;
  heading: string;
  outcome: string;
  imageSrc: string;
  imageAlt: string;
  deepLink: { href: string; label: string };
  demo?: { href: string; label: string };
};

export const SITEGLUE_AGENT_SECTIONS: SiteGlueAgentSection[] = [
  {
    id: "inbox",
    dashboardLabel: "Inbox",
    heading: "Every conversation in one place",
    outcome:
      "Website, phone, and messaging conversations land in one Inbox so your team never hunts across tools.",
    imageSrc: "/images/platform-inbox.png",
    imageAlt: "SiteGlue Inbox showing conversations across channels",
    deepLink: { href: MARKETING_PRODUCT_RESPONSIBILITIES, label: "Communication channels" },
    demo: { href: "/agents", label: "Talk to a live agent" },
  },
  {
    id: "contacts",
    dashboardLabel: "Contacts",
    heading: "People become a durable record",
    outcome:
      "Each person and their agent stay attached to a Contact — not a transcript dump that disappears after the chat.",
    imageSrc: "/images/platform-contacts.png",
    imageAlt: "SiteGlue Contacts list with people and conversation history",
    deepLink: { href: MARKETING_PRODUCT_PROFILE, label: "Agent Profile" },
  },
  {
    id: "profile",
    dashboardLabel: "Profile",
    heading: "One Agent identity and listing consent",
    outcome: `Name, voice, and public listing live on Profile. Moonlight Mode is how you publish ${SITEGLUE_PRODUCT_NAME} to the Directory.`,
    imageSrc: "/images/platform-identity.png",
    imageAlt: "SiteGlue Agent Profile with identity and Moonlight Mode",
    deepLink: { href: MARKETING_PRODUCT_PROFILE, label: "Agent Profile" },
  },
  {
    id: "team",
    dashboardLabel: "Team",
    heading: "Handoff to the right person",
    outcome:
      "Members and locations power Team Handoff™ and Takeover so a human can continue the conversation with full context.",
    imageSrc: "/images/platform-team.png",
    imageAlt: "SiteGlue Team members and locations",
    deepLink: { href: "/team-handoff", label: "Team Handoff™" },
  },
  {
    id: "channels",
    dashboardLabel: "Channels",
    heading: "Chat first. Add channels when you need them.",
    outcome:
      "Base includes AI Agents and Live Chat. Phone, SMS, email, video, WhatsApp, and Messenger are add-ons — only when connected.",
    imageSrc: "/images/platform-locations.png",
    imageAlt: "SiteGlue channel and location settings",
    deepLink: { href: MARKETING_PRODUCT_RESPONSIBILITIES, label: "Communication channels" },
    demo: { href: "/agents", label: "See live channels" },
  },
  {
    id: "training",
    dashboardLabel: "Training",
    heading: "Answers from your website and approved knowledge",
    outcome:
      "The Agent answers from your site and the knowledge you approve. You review before anything goes live.",
    imageSrc: "/images/platform-education.png",
    imageAlt: "SiteGlue Training and knowledge sources",
    deepLink: { href: MARKETING_PRODUCT_TRAINING, label: "Knowledge & Training" },
  },
  {
    id: "tools",
    dashboardLabel: "Tools",
    heading: "Booking and integrations when connected",
    outcome:
      "Calendars and booking tools run only after you connect them. No implied EMR or scheduler until it is configured.",
    imageSrc: "/images/platform-tools.png",
    imageAlt: "SiteGlue Tools and integrations",
    deepLink: { href: MARKETING_PRODUCT_TOOLS, label: "Agent tools" },
  },
];
